/**
 * hospital-profile-template.ts — Dignity's "About this hospital's prices"
 * section, filled from the hospital's live SSR shard at render time.
 *
 * Sibling of cost-template.ts. The spec is scripts/render_hospital_profile.py
 * (tokens_for + render): every token name and every output string here must
 * stay byte-compatible with it, because the writers' checker runs the Python
 * against the same public/data/hospitals/<uuid>/ssr.json this file reads, and a
 * sentence the Python keeps must be the sentence the page shows.
 *
 * Rules carried over unchanged:
 *   - a sentence (or a bullet / heading line) containing a token that cannot be
 *     filled for this hospital today is dropped WHOLE — never "$undefined";
 *   - numbers are formatted the way Python's f-strings format them (round half
 *     to even, thousands separators), so "#3,247 of 4,873" reads identically.
 *
 * Shared by the bot renderer (functions/lib/pages/hospital.ts) and the React
 * page (src/pages/HospitalProfile.tsx → ../../functions/lib/…), so both
 * surfaces render the same words from the same shard.
 */

export interface ProfileScenario { id?: string; name?: string; slug?: string; re?: number | null; rt?: number | null; bp?: number | null; nc?: number | null }
export interface ProfileNeighbour { id?: string; n?: string; c?: string; s?: string; miles?: number | null; cs?: number | null }

/** The hospital SSR shard (public/data/hospitals/<uuid>/ssr.json) — only the keys the tokens read. */
export interface ProfileShard {
  ranking_entry?: Record<string, any> | null;
  meta?: Record<string, any> | null;
  total_hospitals?: number | null;
  mappings_count?: number | null;
  scenarios_top?: ProfileScenario[] | null;
  neighbours_top?: ProfileNeighbour[] | null;
  profile_template?: string | null;
}

export type ProfileTokens = Record<string, string | null | undefined>;

const CMS_PHRASE: Record<string, string> = {
  'Better Than the National Rate': 'better than the national rate',
  'No Different Than the National Rate': 'no different from the national rate',
  'Worse Than the National Rate': 'worse than the national rate',
};

// ── Python-compatible number formatting ──────────────────────────────────

/** Python's round-half-to-even on the exact binary value (`round()` and `.0f`). */
function roundHalfEven(v: number): number {
  const f = Math.floor(v);
  const d = v - f;
  if (d < 0.5) return f;
  if (d > 0.5) return f + 1;
  return f % 2 === 0 ? f : f + 1;
}

/** Python `f'{v:.{digits}f}'`: correctly rounded, ties to even. */
function pyFixed(v: number, digits: number): string {
  const scale = Math.pow(10, digits);
  const scaled = v * scale;
  // JS toFixed picks the larger n on an exact tie; Python picks the even one.
  if (Number.isInteger(scaled * 2) && !Number.isInteger(scaled)) {
    const n = roundHalfEven(scaled);
    return (n / scale).toFixed(digits);
  }
  return v.toFixed(digits);
}

/** Thousands separators on the integer part, Python `:,` style. */
function group(s: string): string {
  const neg = s.startsWith('-');
  const body = neg ? s.slice(1) : s;
  const [int, frac] = body.split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (neg ? '-' : '') + grouped + (frac != null ? `.${frac}` : '');
}

/** Python `f'{n:,}'` for an integer. */
const commaInt = (n: number) => group(String(Math.trunc(n)));

function num(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
/** Python truthiness for the values the shard carries (0, '', null are falsy). */
function truthy(v: unknown): boolean {
  return !(v == null || v === 0 || v === '' || v === false || (Array.isArray(v) && v.length === 0));
}

/** Python `f'{v:+.0f}%'` — the sign follows the float, so -0.4 is "-0%". */
function signedPct(v: unknown): string | null {
  const n = num(v);
  if (n == null) return null;
  const r = pyFixed(Math.abs(n), 0);
  const neg = n < 0 || Object.is(n, -0);
  return `${neg ? '-' : '+'}${r}%`;
}

/** "43% below the national median" / "120% above the national median". */
function markupPhrase(v: unknown): string | null {
  const n = num(v);
  if (n == null) return null;
  if (Math.abs(n) < 5) return 'within 5% of the national median';
  if (n < 0) return `${pyFixed(Math.abs(n), 0)}% below the national median`;
  return `${pyFixed(n, 0)}% above the national median`;
}

/** Python `str.isupper()`: at least one cased char and no lowercase ones. */
function isUpper(s: string): boolean {
  return s.toUpperCase() === s && s.toLowerCase() !== s;
}
const SHOUT_FIX: Record<string, string> = {
  Hsp: 'Hospital', Hosp: 'Hospital', Med: 'Medical', Ctr: 'Center', Cntr: 'Center',
  Reg: 'Regional', Mem: 'Memorial', Hlth: 'Health', Univ: 'University', Gen: 'General',
  Comm: 'Community', Dist: 'District', Sys: 'System', Ctrs: 'Centers', Svcs: 'Services',
  Of: 'of', At: 'at', And: 'and', The: 'the', For: 'for', In: 'in', On: 'on',
};
const SHOUT_KEEP = new Set([
  'LLC', 'LP', 'PC', 'VA', 'ER', 'ICU', 'HCA', 'CHI', 'SSM', 'UPMC', 'UNC', 'USC', 'UCLA', 'UCSF', 'UCSD', 'UC',
  'NYU', 'LSU', 'OSF', 'TMC', 'AMG', 'UMC', 'MD', 'DC', 'US', 'NW', 'SW', 'NE', 'SE', 'II', 'III', 'IV',
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
  'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA',
  'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
]);
const stripPunct = (w: string) => w.replace(/^[.,()]+/, '').replace(/[.,()]+$/, '');
const isAlpha = (w: string) => /^\p{L}+$/u.test(w);
/** Python str.capitalize(): first char upper, the rest lower. */
const capitalize = (w: string) => w.slice(0, 1).toUpperCase() + w.slice(1).toLowerCase();

/** Title-case a SHOUTED name without mangling apostrophes or well-known initialisms (mirrors tc() in the Python). */
function tc(v: unknown): string | null {
  if (v == null || v === '') return v == null ? null : '';
  const s = String(v);
  if (!isUpper(s)) return s;
  const out: string[] = [];
  const words = s.trim() === '' ? [] : s.trim().split(/\s+/);
  words.forEach((w, i) => {
    const core = stripPunct(w);
    if (SHOUT_KEEP.has(core) || (core.length <= 3 && !isAlpha(core))) { out.push(w); return; }
    let t = w.slice(0, 1).toUpperCase() + w.slice(1).toLowerCase();
    // O'BRIEN -> O'Brien, CHILDREN'S -> Children's, ST. MARY'S -> St. Mary's
    t = t.replace(/(?<=[A-Za-z])'(?=[a-z])/g, "'");
    const tl = t.toLowerCase();
    const oPrefix = t.length > 3 && (tl.startsWith("o'") || tl.startsWith("d'") || tl.startsWith("l'"));
    t = t.replace(/\b([A-Za-z])'([A-Za-z])/g, (m0, a: string, b: string) => (oPrefix ? `${a}'${b.toUpperCase()}` : m0));
    t = t.replace(/(?<=\bMc)([a-z])/g, (_m, c: string) => c.toUpperCase());
    t = t.replace(/(?<=[a-z])-([a-z])/g, (_m, c: string) => `-${c.toUpperCase()}`); // Center-drew -> Center-Drew
    const key = stripPunct(t);
    if (Object.prototype.hasOwnProperty.call(SHOUT_FIX, key)) {
      t = t.split(key).join(i ? SHOUT_FIX[key] : capitalize(SHOUT_FIX[key]));
    }
    out.push(t);
  });
  return out.join(' ');
}

/**
 * A scenario comparison we can defend: the multiple of the national figure,
 * never the dollars (scenario totals from the pricing lookup are not safe as
 * absolute amounts). Withheld below 1.5× and above 25×.
 */
function multiplePhrase(bp: unknown, nc: unknown): string | null {
  const b = num(bp), n = num(nc);
  if (b == null || n == null) return null;
  if (n <= 0 || b <= 0) return null;
  const m = b / n;
  if (m < 1.5 || m > 25) return null;
  return m >= 2 ? `about ${pyFixed(m, 0)} times the national figure` : 'about half again the national figure';
}

/** ratio = rank / hospitals ranked, 0 = most expensive. */
function bandPhrase(ratio: number): string {
  return ratio <= 0.10 ? 'the most expensive 10% of hospitals we rank for it'
    : ratio <= 0.25 ? 'the most expensive quarter of hospitals we rank for it'
    : ratio <= 0.50 ? 'the pricier half of hospitals we rank for it'
    : ratio <= 0.75 ? 'the cheaper half of hospitals we rank for it'
    : ratio <= 0.90 ? 'the cheapest quarter of hospitals we rank for it'
    : 'the cheapest 10% of hospitals we rank for it';
}

// ── tokens_for ───────────────────────────────────────────────────────────

const MARKUP_COLS: Array<[string, string]> = [
  ['median_markup', 'median_gross_markup_percentage'],
  ['cpt_markup', 'cpt_median_markup_percentage'],
  ['drg_markup', 'drg_median_markup_percentage'],
  ['rev_markup', 'rev_median_markup_percentage'],
  ['hcpcs_markup', 'hcpcs_median_markup_percentage'],
];
const COUNT_COLS: Record<string, string> = {
  cpt_markup: 'cpt_count', drg_markup: 'drg_count', rev_markup: 'revenue_count', hcpcs_markup: 'hcpcs_procedure_count',
};
const CATS: Record<string, string> = {
  cpt_markup: 'procedures and visits', drg_markup: 'inpatient stays',
  rev_markup: 'room, board and facility charges', hcpcs_markup: 'drugs, devices and supplies',
};

/** Every token's value for this hospital today. Mirrors tokens_for() in the Python. */
export function profileTokens(d: ProfileShard): ProfileTokens {
  const r = d.ranking_entry || {};
  const m = d.meta || {};
  const total = d.total_hospitals;
  const rank = r.overall_rank_gross;
  const t: ProfileTokens = {};
  t.hospital = tc(m.name);
  t.city = tc(m.city);
  t.state = m.state ?? null;
  t.beds = truthy(m.bed_count) ? commaInt(Number(m.bed_count)) : null;
  t.procedures_priced = truthy(d.mappings_count) ? commaInt(Number(d.mappings_count)) : null;
  t.price_file_year = truthy(m.data_vintage_year) ? String(m.data_vintage_year) : null;
  t.cms_as_of = m.cms_as_of_date ?? null;

  // price position — never a rank without its denominator
  if (truthy(rank) && truthy(total)) {
    const rk = Math.trunc(Number(rank));
    const tot = Math.trunc(Number(total));
    t.price_rank = `#${commaInt(rk)}`;
    t.graded_hospitals = commaInt(tot);
    const pct = (100.0 * rk) / tot;
    t.price_quartile = pct <= 25 ? 'the most expensive quarter of the US hospitals we grade'
      : pct <= 50 ? 'the pricier half of the US hospitals we grade'
      : pct <= 75 ? 'the cheaper half of the US hospitals we grade'
      : 'the cheapest quarter of the US hospitals we grade';
    const dearer = rk - 1;
    t.dearer_count = dearer === 0 ? 'no hospital we grade charges more'
      : dearer === 1 ? 'only one charges more'
      : `${commaInt(dearer)} charge more`;
    const cheaper = tot - rk;
    t.cheaper_count = commaInt(cheaper);
    // decile bands — the 10% framing Trent asked for; quartile is the coarser fallback
    t.price_band = pct <= 10 ? 'the most expensive 10% of the US hospitals we grade'
      : pct <= 25 ? 'the most expensive quarter of the US hospitals we grade'
      : pct <= 50 ? 'the pricier half of the US hospitals we grade'
      : pct <= 75 ? 'the cheaper half of the US hospitals we grade'
      : pct <= 90 ? 'the cheapest quarter of the US hospitals we grade'
      : 'the cheapest 10% of the US hospitals we grade';
    t.price_percentile = `${pyFixed(100 - pct, 0)}%`; // share of graded hospitals that charge less
  }
  for (const [key, col] of MARKUP_COLS) {
    let v: unknown = r[col];
    // a category the hospital does not price at all reports 0.0 with a 0 count
    const cntCol = COUNT_COLS[key];
    if (cntCol && !truthy(r[cntCol])) v = null;
    t[key] = signedPct(v);
    t[`${key}_phrase`] = markupPhrase(v);
  }
  // the category contrast as tokens, so the "split" lead can never go stale
  const vals: Array<[string, number]> = [];
  for (const [key, col] of MARKUP_COLS.slice(1)) {
    if (t[key] != null) vals.push([key, Number(r[col])]);
  }
  if (vals.length >= 2) {
    let hi = vals[0], lo = vals[0];
    for (const v of vals) { if (v[1] > hi[1]) hi = v; if (v[1] < lo[1]) lo = v; }
    t.widest_category = CATS[hi[0]]; t.widest_category_phrase = markupPhrase(hi[1]);
    t.narrowest_category = CATS[lo[0]]; t.narrowest_category_phrase = markupPhrase(lo[1]);
    t.category_split = hi[1] - lo[1] >= 25
      ? `${CATS[hi[0]]} ${markupPhrase(hi[1])}, while ${CATS[lo[0]]} come in ${markupPhrase(lo[1])}`
      : Math.abs(hi[1] - lo[1]) < 5
        ? `${CATS[hi[0]]} and ${CATS[lo[0]]} both sit ${markupPhrase(hi[1])}`
        : `${CATS[hi[0]]} ${markupPhrase(hi[1])} and ${CATS[lo[0]]} ${markupPhrase(lo[1])}`;
  }
  if (r.expensive_procedures_percentage != null && truthy(r.total_procedures)) {
    t.expensive_share = `${pyFixed(Number(r.expensive_procedures_percentage), 0)}%`;
  }

  // quality — prefer the percentage, fall back to stars, never invent
  t.cms_stars = truthy(m.cms_overall_rating) ? String(m.cms_overall_rating) : null;
  t.recommend_pct = truthy(m.hcahps_recommend_pct) ? `${pyFixed(Number(m.hcahps_recommend_pct), 0)}%` : null;
  t.hcahps_surveys = truthy(m.hcahps_surveys) ? commaInt(Number(m.hcahps_surveys)) : null;
  t.mortality = CMS_PHRASE[m.cms_mortality_rating || ''] ?? null;
  t.readmission = CMS_PHRASE[m.cms_readmission_rating || ''] ?? null;
  t.safety = CMS_PHRASE[m.cms_safety_rating || ''] ?? null;
  t.google_rating = truthy(m.google_rating) ? pyFixed(Number(m.google_rating), 1) : null;
  t.google_reviews = truthy(m.google_review_count) ? commaInt(Number(m.google_review_count)) : null;

  // scenario examples: most expensive-ranked first (re = rank among rt hospitals)
  let sc = (d.scenarios_top || []).filter(s => truthy(s.re) && truthy(s.rt) && Number(s.rt) >= 50);
  const big = sc.filter(s => Number(s.rt) >= 300);
  if (big.length >= 3) sc = big; // prefer care with a real comparison pool
  const ratio = (s: ProfileScenario) => Number(s.re) / Number(s.rt);
  const scExp = [...sc].sort((a, b) => ratio(a) - ratio(b));
  const scCheap = [...sc].sort((a, b) => ratio(b) - ratio(a));
  scExp.slice(0, 3).forEach((s, idx) => {
    const i = idx + 1;
    t[`priciest_scenario_${i}`] = s.name ?? null;
    t[`priciest_scenario_${i}_rank`] = `#${commaInt(Number(s.re))}`;
    t[`priciest_scenario_${i}_of`] = commaInt(Number(s.rt));
    t[`priciest_scenario_${i}_multiple`] = multiplePhrase(s.bp, s.nc);
    t[`priciest_scenario_${i}_band`] = bandPhrase(ratio(s));
  });
  scCheap.slice(0, 3).forEach((s, idx) => {
    const i = idx + 1;
    if (ratio(s) < 0.7) return; // only call it "cheap" if it sits in the cheaper 30%
    t[`cheapest_scenario_${i}`] = s.name ?? null;
    t[`cheapest_scenario_${i}_rank`] = `#${commaInt(Number(s.re))}`;
    t[`cheapest_scenario_${i}_of`] = commaInt(Number(s.rt));
    t[`cheapest_scenario_${i}_multiple`] = multiplePhrase(s.bp, s.nc);
    t[`cheapest_scenario_${i}_band`] = bandPhrase(ratio(s));
  });

  // nearest neighbour that beats this hospital on at least one scenario
  const nb = (d.neighbours_top || []).filter(n => truthy(n.cs))
    .sort((a, b) => (a.miles ?? 1e9) - (b.miles ?? 1e9));
  if (nb.length) {
    const n = nb[0];
    t.nearest_cheaper_hospital = tc(n.n);
    t.nearest_cheaper_city = tc(n.c);
    t.nearest_cheaper_miles = pyFixed(Number(n.miles), 0);
    const mi = Math.max(1, roundHalfEven(Number(n.miles)));
    t.nearest_cheaper_distance = mi === 1 ? `${mi} mile` : `${mi} miles`;
    t.nearest_cheaper_scenarios = commaInt(Number(n.cs));
  }
  t.neighbours_count = truthy(d.neighbours_top) ? commaInt((d.neighbours_top as ProfileNeighbour[]).length) : null;
  return t;
}

// ── render ───────────────────────────────────────────────────────────────

const TOKEN_RE = /\{\{\s*([A-Za-z0-9_|]+)\s*\}\}/g;
const SENT_SPLIT = /(?<=[.!?])\s+(?=[A-Z"“(*\-{])/;

export interface ProfileRender { text: string; dropped: Array<{ sentence: string; missing: string[] }> }

/**
 * Fill every {{token}}; a sentence / bullet / heading with an unfillable token
 * is dropped whole. Mirrors render() in the Python line for line.
 */
export function renderProfileTemplate(template: string, tokens: ProfileTokens): ProfileRender {
  const dropped: ProfileRender['dropped'] = [];
  const outLines: string[] = [];
  for (const line of template.split('\n')) {
    if (!line.trim()) { outLines.push(line); continue; }
    // bullets / headings are one "sentence" each; prose is split on sentence ends
    const first = line.replace(/^\s+/, '')[0];
    const units = first === '-' || first === '*' || first === '#' ? [line] : line.split(SENT_SPLIT);
    const kept: string[] = [];
    for (const u of units) {
      const names = Array.from(u.matchAll(TOKEN_RE), mm => mm[1]);
      const missing = names.filter(name => tokens[name] == null || tokens[name] === '');
      if (missing.length) { dropped.push({ sentence: u.trim(), missing }); continue; }
      kept.push(u.replace(TOKEN_RE, (_m, name: string) => String(tokens[name])));
    }
    outLines.push(kept.join(' '));
  }
  return { text: outLines.join('\n'), dropped };
}

/** True when the stored copy is a template (the page-level guard, same as scenarios). */
export function isProfileTemplate(md: string | null | undefined): md is string {
  return typeof md === 'string' && md.includes('{{');
}

/** One call for both surfaces: null when there is nothing to show. */
export function renderHospitalProfile(shard: ProfileShard | null | undefined): string | null {
  const md = shard?.profile_template;
  if (!shard || !isProfileTemplate(md)) return null;
  const { text } = renderProfileTemplate(md, profileTokens(shard));
  return text.trim() ? text : null;
}
